/** Mirrors trucision-backend-nest src/knowledge-hub/knowledge-hub.constants.ts and dto/. */

export const KNOWLEDGE_HUB_CATEGORIES = [
  "Maintenance",
  "User Manual",
  "Safety Guidelines",
  "Troubleshooting",
  "SOP",
  "Technical Know-hows",
  "Others",
] as const;
export type KnowledgeHubCategory = (typeof KNOWLEDGE_HUB_CATEGORIES)[number];

export type KnowledgeHubKind = "file" | "video_url" | "link";
export type KnowledgeHubExpiryState =
  "active" | "expired" | "pending_reapproval";

/** Mirrors trucision-backend-nest's KNOWLEDGE_HUB_VIEW_ONLY_ROLES — gates the Upload File
 * button, which has no per-file canEdit flag to key off (KH-RBAC-011). The server enforces this
 * independently on POST /knowledge-hub/files; this only controls whether the button is shown.
 * PRD V1.1 §6 RBAC (updated 2026-09-25): Lab Operator is view-only, every other role has full
 * access — deny-list, not allow-list. */
const KNOWLEDGE_HUB_VIEW_ONLY_ROLES = new Set(["LAB_OPERATOR"]);

export function isKnowledgeHubHighLevelRole(role: unknown): boolean {
  return (
    typeof role === "string" &&
    !KNOWLEDGE_HUB_VIEW_ONLY_ROLES.has(role.toUpperCase())
  );
}

export type KnowledgeHubAttachment = {
  key?: string;
  name?: string;
  url?: string;
  contentType?: string;
  size?: number;
};

export type KnowledgeHubFile = {
  id: string;
  organizationId: string;
  instrumentTypeId: string;
  instrumentTypeName: string | null;
  labId: string;
  labName: string | null;
  oemId: string | null;
  instrumentId: string | null;
  kind: KnowledgeHubKind;
  category: KnowledgeHubCategory;
  customCategory: string | null;
  fileName: string;
  version: number;
  language: string;
  note: string | null;
  attachment: KnowledgeHubAttachment | null;
  linkUrl: string | null;
  videoUrl: string | null;
  expiryAt: string;
  expiryState: KnowledgeHubExpiryState;
  viewCount: number;
  downloadCount: number;
  createdBy: string;
  createdAt: string;
  canEdit: boolean;
  canDelete: boolean;
  /** PRD: Knowledge Hub V1.1 §6 RBAC (updated 2026-09-15) — Lab Operator and User can View but not Download. */
  canDownload: boolean;
};

export type KnowledgeHubInstrumentTypeCard = {
  id: string;
  name: string;
  fileCount: number;
  imageUrl: string | null;
};

export type KnowledgeHubOptions = {
  instrumentTypes: KnowledgeHubInstrumentTypeCard[];
  oems: Array<{ id: string; name: string }>;
  labs: Array<{ id: string; name: string }>;
  instruments: Array<{ id: string; name: string; labId: string }>;
  categories: readonly string[];
  languages: Array<{ code: string; name: string }>;
};

export type KnowledgeHubActivityAction =
  "UPLOAD" | "VIEW" | "DOWNLOAD" | "DELETE" | "REAPPROVE";

export type KnowledgeHubActivityItem = {
  id: string;
  fileId: string;
  instrumentTypeId: string;
  instrumentTypeName: string | null;
  labId: string;
  labName: string | null;
  fileName: string;
  action: KnowledgeHubActivityAction;
  actorName: string;
  actorRole: string | null;
  createdAt: string;
};

export type Paginated<T> = {
  data: T[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
};
